import React, { useRef, useMemo, useEffect, useState } from 'react'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { useResponsive } from 'hooks/useResponsive'

import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { searchResultPlusMoreClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import { checkMinBreakpoint } from 'lib/responsiveUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import pluralize from 'lib/pluralize'
import { getOfferTileInclusionIcon } from 'lib/getOfferTileInclusionIcon'

const Root = styled.div`
  position: relative;
  margin-top: ${rem(16)};
  overflow: hidden;
  max-height: ${rem(170)};

  ${mediaQueryUp.sm} {
    max-height: ${rem(220)};

    &.landing-page-test {
      max-height: ${rem(140)};
    }
  }
`

const Mask = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: ${rem(32)} 0 0;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 50%);
`

const MoreInclusions = styled.div`
margin-top: ${rem(4)};
`

const StyledInclusionItemDiv = styled.div`
  display: flex;
`

const InclusionItemText = styled(BodyText)`
  margin-top: 0;
  padding-left: ${rem(8)};
  text-indent: ${rem(-2)};
`

const StyledIconDiv = styled.div`
  position: relative;
  top: 2px;
`

interface Props {
  className?: string;
  content?: string;
  tileInclusionsList?: App.TileInclusion[];
  heading: string;
  abTestOfferTile?: boolean;
  maxInclusionsCount?: number;
}

function OfferTileInclusions({
  className,
  content,
  tileInclusionsList,
  heading = 'Your handpicked inclusions',
  abTestOfferTile,
  maxInclusionsCount,
}: Props) {
  const [inclusionsHeight, setInclusionsHeight] = useState(null)

  const showReducedInclusions = abTestOfferTile || !!maxInclusionsCount

  const inclusionsEl = useRef(null)
  const [match] = useResponsive()
  const shownInclusions = tileInclusionsList?.slice(0, maxInclusionsCount ?? 3)
  const numInclusionsLeft = tileInclusionsList?.length - maxInclusionsCount ?? 3

  useEffect(() => {
    setInclusionsHeight(inclusionsEl.current?.clientHeight)
  }, [])

  const maxInclusionsHeight = useMemo(() => checkMinBreakpoint(match, 'sm') ? 220 : 170, [match])

  return (
    <Root className={cn(className, { 'landing-page-test': abTestOfferTile })}>
      <BodyText variant="medium" weight="bold">{heading}</BodyText>
      <div ref={inclusionsEl}>
        {content && !tileInclusionsList?.length && <MarkdownRender fontSize="medium" type="compact" content={content} />}
        {tileInclusionsList?.length > 0 &&
         (showReducedInclusions ? shownInclusions : tileInclusionsList)?.map((inclusion) => {
           const InclusionIcon = getOfferTileInclusionIcon(inclusion.icon)

           return (
             <StyledInclusionItemDiv key={inclusion.text}>
               <StyledIconDiv>
                 <InclusionIcon data-testid={inclusion.icon || 'default_icon'} size={16} />
               </StyledIconDiv>
               <InclusionItemText
                  variant="medium"
                  weight={inclusion.isHighlighted ? 'bold' : 'normal'}
                >
                 {inclusion.text}
               </InclusionItemText>
             </StyledInclusionItemDiv>
           )
         })}
      </div>
      {inclusionsHeight > maxInclusionsHeight && !showReducedInclusions &&
        <Mask onClick={() => { addGTMEvent(searchResultPlusMoreClick()) }}>
          <BodyText variant="medium" weight="bold" colour="primary">Plus more...</BodyText>
        </Mask>
      }
      {showReducedInclusions && numInclusionsLeft > 0 &&
        <MoreInclusions>
          <BodyText variant="medium" weight="bold" colour="primary" underline>{`+ ${numInclusionsLeft} more ${pluralize({ singular: 'inclusion', count: numInclusionsLeft }).text}`}</BodyText>
        </MoreInclusions>
      }
    </Root>
  )
}

export default OfferTileInclusions
